import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Popover,
  Snackbar,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const AlertsIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 9V13" />
    <path d="M12 17H12.01" />
    <path d="M10.7307 2.76084L2.24095 17.3945C1.49255 18.6846 2.42338 20.3 3.91019 20.3H20.0897C21.5765 20.3 22.5074 18.6846 21.759 17.3945L13.2692 2.76084C12.5258 1.47972 11.4741 1.47972 10.7307 2.76084Z" />
  </svg>
);

const OpenActionIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M5 12h12" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const useStyles = makeStyles(theme => ({
  popoverPaper: {
    width: 420,
    maxWidth: "calc(100vw - 24px)",
    padding: theme.spacing(1),
    borderRadius: 14
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: theme.palette.text.primary,
    padding: theme.spacing(1, 1, 0.5)
  },
  sectionHint: {
    padding: theme.spacing(0, 1, 1),
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  list: {
    maxHeight: 260,
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  item: {
    borderRadius: 10,
    marginBottom: theme.spacing(0.5),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "rgba(20, 40, 70, 0.04)"
  },
  itemContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    width: "100%"
  },
  actionPill: {
    minWidth: 78,
    height: 34,
    borderRadius: 999,
    padding: theme.spacing(0, 1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.02em",
    color: theme.palette.type === "dark" ? "#d8ecff" : "#24507a",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(52, 98, 148, 0.92) 0%, rgba(31, 73, 122, 0.92) 100%)"
      : "linear-gradient(135deg, rgba(226, 239, 251, 0.98) 0%, rgba(201, 224, 245, 0.98) 100%)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(170, 211, 255, 0.20)"
      : "1px solid rgba(36, 80, 122, 0.14)",
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 18px rgba(0,0,0,0.18)"
      : "0 8px 18px rgba(22, 49, 73, 0.10)",
    flexShrink: 0
  },
  snackbarRoot: {
    top: 84
  },
  snackbarCard: {
    minWidth: 360,
    maxWidth: 460,
    borderRadius: 18,
    padding: theme.spacing(1.5, 2),
    boxShadow: theme.palette.type === "dark"
      ? "0 18px 40px rgba(0,0,0,0.42)"
      : "0 18px 40px rgba(22,49,73,0.18)",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(127,168,204,0.26)"
      : "1px solid rgba(22,49,73,0.12)",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, rgba(22,39,52,0.98) 0%, rgba(29,52,70,0.98) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(242,247,252,0.98) 100%)"
  },
  snackbarEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.type === "dark" ? "#8EC5FF" : "#2F6EA4",
    marginBottom: theme.spacing(0.5)
  },
  snackbarTitle: {
    fontSize: 22,
    lineHeight: 1.1,
    fontWeight: 800,
    color: theme.palette.type === "dark" ? "#F4F7FA" : "#163149"
  },
  snackbarBody: {
    marginTop: theme.spacing(1),
    fontSize: 14,
    lineHeight: 1.45,
    color: theme.palette.type === "dark" ? "rgba(244,247,250,0.86)" : "#3A556A"
  },
  snackbarActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1)
  }
}));

const formatDate = value => {
  if (!value) return "Sin fecha";
  try {
    return new Date(value).toLocaleString("es-CO");
  } catch (_error) {
    return value;
  }
};

const buildPopupStorageKey = (companyId, userId) =>
  `operationalAlertsPopupLastShown:${companyId || "0"}:${userId || "0"}`;

const formatThresholdLabel = minutes => {
  const safeMinutes = Math.max(1, Number(minutes || 0) || 0);
  if (safeMinutes >= 60 && safeMinutes % 60 === 0) {
    const hours = safeMinutes / 60;
    return `${hours} hora${hours === 1 ? "" : "s"}`;
  }
  return `${safeMinutes} minuto${safeMinutes === 1 ? "" : "s"}`;
};

export default function OperationalAlertsPopover() {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { getAll } = useCompanySettings();
  const [anchorEl, setAnchorEl] = useState(null);
  const iconButtonRef = useRef(null);
  const [data, setData] = useState({
    staleMinutes: 240,
    recentContactsDays: 30,
    summary: { staleTicketsCount: 0, unnamedContactsCount: 0, totalIssues: 0 },
    staleTickets: [],
    unnamedContacts: []
  });
  const [popupConfig, setPopupConfig] = useState({
    enabled: false,
    intervalMinutes: 30
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [pulseOn, setPulseOn] = useState(true);
  const previousCountRef = useRef(null);

  const currentTicketUuid = useMemo(() => {
    const pathParts = history.location.pathname.split("/");
    return pathParts[1] === "tickets" && pathParts[2] ? pathParts[2] : undefined;
  }, [history.location.pathname]);

  const shouldShowPopup = nextTotal => {
    if (!popupConfig.enabled || nextTotal <= 0 || !user?.id) {
      return false;
    }

    const storageKey = buildPopupStorageKey(user.companyId, user.id);
    const lastShownAt = Number(localStorage.getItem(storageKey) || "0");
    const intervalMs = Math.max(5, Number(popupConfig.intervalMinutes || 30) || 30) * 60 * 1000;

    return !lastShownAt || (Date.now() - lastShownAt) >= intervalMs;
  };

  const markPopupShown = () => {
    if (!user?.id) return;
    localStorage.setItem(buildPopupStorageKey(user.companyId, user.id), String(Date.now()));
  };

  const loadAlerts = async ({ silent = false, allowPopup = true } = {}) => {
    try {
      const response = await api.get("/operational-alerts/summary", {
        params: {
          currentTicketUuid,
          staleMinutes: Math.max(5, Number(user?.operationalVisualAlertAfterMinutes || 240) || 240)
        }
      });
      const nextData = response.data || {};
      const nextTotal = Number(nextData?.summary?.totalIssues || 0);
      const prevTotal = previousCountRef.current;

      setData({
        staleMinutes: Number(nextData.staleMinutes || 240),
        recentContactsDays: Number(nextData.recentContactsDays || 30),
        summary: nextData.summary || { staleTicketsCount: 0, unnamedContactsCount: 0, totalIssues: 0 },
        staleTickets: Array.isArray(nextData.staleTickets) ? nextData.staleTickets : [],
        unnamedContacts: Array.isArray(nextData.unnamedContacts) ? nextData.unnamedContacts : []
      });

      if (nextTotal <= 0) {
        setSnackbarOpen(false);
      }

      if (!silent && prevTotal !== null && nextTotal > prevTotal) {
        toast.info(
          `Pendientes operativos: ${nextData.summary.staleTicketsCount} ticket(s) sin cerrar y ${nextData.summary.unnamedContactsCount} contacto(s) sin nombre.`
        );
      }

      if (allowPopup && shouldShowPopup(nextTotal)) {
        markPopupShown();
        setSnackbarOpen(true);
      }

      previousCountRef.current = nextTotal;
    } catch (err) {
      if (!silent) {
        toastError(err);
      }
    }
  };

  useEffect(() => {
    if (!user?.id) return undefined;

    getAll(user.companyId)
      .then(settings => {
        setPopupConfig({
          enabled: Boolean(settings?.operationalAlertsPopupEnabled) && user?.operationalVisualAlertsEnabled !== false,
          intervalMinutes: Math.max(5, Number(settings?.operationalAlertsPopupIntervalMinutes || 30) || 30)
        });
      })
      .catch(() => {
        setPopupConfig({
          enabled: Boolean(user?.operationalVisualAlertsEnabled),
          intervalMinutes: 30
        });
      });

    loadAlerts({ silent: true, allowPopup: true });
    const interval = setInterval(() => loadAlerts(), 90000);
    return () => clearInterval(interval);
  }, [user?.id, user?.companyId, currentTicketUuid, getAll]);

  useEffect(() => {
    if (!user?.id || !popupConfig.enabled) return;
    loadAlerts({ silent: true, allowPopup: true });
  }, [popupConfig.enabled, popupConfig.intervalMinutes, user?.id]);

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
    loadAlerts({ silent: true });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleOpenFromSnackbar = () => {
    setSnackbarOpen(false);
    setAnchorEl(iconButtonRef.current);
    loadAlerts({ silent: true });
  };

  const openTicket = ticket => {
    handleClose();
    history.push(`/tickets/${ticket.uuid}`);
  };

  const openContactDirectory = contact => {
    localStorage.setItem("contactsSearchPrefill", contact.number || contact.name || "");
    handleClose();
    history.push("/contacts");
  };

  const totalIssues = Number(data?.summary?.totalIssues || 0);

  useEffect(() => {
    if (!totalIssues) {
      setPulseOn(false);
      return undefined;
    }

    setPulseOn(true);
    const interval = setInterval(() => {
      setPulseOn(prev => !prev);
    }, 850);

    return () => clearInterval(interval);
  }, [totalIssues]);

  return (
    <>
      <IconButton
        ref={iconButtonRef}
        onClick={handleOpen}
        style={{
          color: totalIssues ? "#eef8ff" : "white",
          background: totalIssues ? (pulseOn ? "rgba(0, 119, 182, 0.94)" : "rgba(255, 196, 61, 0.94)") : "transparent",
          border: totalIssues ? "1px solid rgba(214, 240, 255, 0.82)" : "1px solid transparent",
          boxShadow: totalIssues ? (pulseOn ? "0 0 0 5px rgba(0, 119, 182, 0.20)" : "0 0 0 2px rgba(255, 196, 61, 0.18)") : "none",
          transform: totalIssues ? (pulseOn ? "scale(1.08)" : "scale(1)") : "scale(1)",
          transition: "all 180ms ease"
        }}
        title="Alertas operativas"
      >
        <Badge badgeContent={totalIssues} color="secondary">
          <AlertsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 700, padding: 8 }}>
          Alertas operativas
        </Typography>

        <Typography variant="body2" className={classes.sectionHint}>
          Este bloque revisa tickets con más de {formatThresholdLabel(data.staleMinutes)} sin movimiento y contactos recientes que siguen sin nombre.
        </Typography>

        <Divider />

        <Typography className={classes.sectionTitle}>
          Tickets sin cerrar: {data.summary.staleTicketsCount}
        </Typography>
        <List dense className={classes.list}>
          {data.staleTickets.length ? data.staleTickets.map(ticket => (
            <ListItem
              button
              key={ticket.id}
              className={classes.item}
              onClick={() => openTicket(ticket)}
            >
              <div className={classes.itemContent}>
                <ListItemText
                  primary={`${ticket.contact?.name || "Sin nombre"}${ticket.queue?.name ? ` · ${ticket.queue.name}` : ""}`}
                  secondary={(
                    <>
                      <span>{ticket.contact?.number || "Sin numero"} · {ticket.status}</span>
                      <br />
                      <span>Última actividad: {formatDate(ticket.updatedAt)}</span>
                    </>
                  )}
                />
                <div className={classes.actionPill}>
                  <OpenActionIcon />
                  Abrir
                </div>
              </div>
            </ListItem>
          )) : (
            <ListItem>
              <ListItemText primary="No hay tickets atrasados por ahora." />
            </ListItem>
          )}
        </List>

        <Divider />

        <Typography className={classes.sectionTitle}>
          Contactos sin nombre: {data.summary.unnamedContactsCount}
        </Typography>
        <List dense className={classes.list}>
          {data.unnamedContacts.length ? data.unnamedContacts.map(contact => (
            <ListItem
              button
              key={contact.id}
              className={classes.item}
              onClick={() => openContactDirectory(contact)}
            >
              <div className={classes.itemContent}>
                <ListItemText
                  primary={contact.name || "Sin nombre"}
                  secondary={(
                    <>
                      <span>{contact.number || "Sin numero"} · Atenciones: {contact.attendedCount}</span>
                      <br />
                      <span>Última atención: {formatDate(contact.latestAttentionAt)}</span>
                    </>
                  )}
                />
                <div className={classes.actionPill}>
                  <OpenActionIcon />
                  Abrir
                </div>
              </div>
            </ListItem>
          )) : (
            <ListItem>
              <ListItemText primary="No hay contactos pendientes de nombrar." />
            </ListItem>
          )}
        </List>

        <div className={classes.footer}>
          <Button size="small" variant="outlined" color="primary" onClick={() => { handleClose(); history.push("/tickets"); }}>
            Ver tickets
          </Button>
          <Button size="small" variant="outlined" color="primary" onClick={() => { handleClose(); history.push("/contacts"); }}>
            Ver contactos
          </Button>
        </div>
      </Popover>

      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={12000}
        className={classes.snackbarRoot}
      >
        <Box className={classes.snackbarCard}>
          <Typography className={classes.snackbarEyebrow}>
            Alerta operativa
          </Typography>
          <Typography className={classes.snackbarTitle}>
            {totalIssues} pendiente{totalIssues === 1 ? "" : "s"} por revisar
          </Typography>
          <Typography className={classes.snackbarBody}>
            {data.summary.staleTicketsCount} ticket(s) sin cerrar y {data.summary.unnamedContactsCount} contacto(s) sin nombre siguen pendientes. Este aviso se repetira cada {popupConfig.intervalMinutes} minutos mientras existan casos sin revisar.
          </Typography>
          <div className={classes.snackbarActions}>
            <Button size="small" onClick={handleSnackbarClose}>
              Posponer
            </Button>
            <Button size="small" variant="contained" color="primary" onClick={handleOpenFromSnackbar}>
              Ver alertas
            </Button>
          </div>
        </Box>
      </Snackbar>
    </>
  );
}
